import * as React from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  type Announcements,
  type Collision,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
  type DropAnimationFunction,
  type KeyboardCoordinateGetter,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { cn } from "@/lib/utils";

import {
  donorHandleClasses,
  DRAG_HANDLE_INSTRUCTIONS,
  DROP_EASING,
  type DonorDragPhase,
  LIFTED_Z_INDEX,
  settleOver,
  SETTLING_Z_INDEX,
  SIBLING_SHIFT,
  useDonorDragBodyRules,
  useDonorDragKeys,
  useDonorFluidAutoScroll,
  useDonorSensors,
} from "./dnd-sensors";
import { MuiDehazeIcon, MuiDragIndicatorIcon } from "./mui-spec-icons";

// The sort popover's reorderable rows — the donor's `DraggableSortRow` inside a
// `react-beautiful-dnd` `Droppable` (`sort-popover.tsx:236-425`,
// `StrictModeDroppable.tsx`) — as a `@dnd-kit` sortable (#679 Q8) on the
// sensors, keys and motion the image strip already ported (`dnd-sensors.ts`).
//
// The row (`sort-popover.tsx:327-414`): an outer box 8px above the next; inside
// it an outlined `Paper` (`role="listitem"`, 1px `divider`, radius 4,
// `px 8 py 4`, flex, centred, gap 8) that goes to `action.hover` with
// `shadows[4]` while lifted, over its own 300ms box-shadow transition. In it:
//
//   - **the handle**, and only the handle starts a drag — rbd's
//     `dragHandleProps` (`tabIndex 0`, `role="button"`, the instructions as its
//     description) plus the donor's `aria-label` "Drag to reorder {label}"; a
//     small (20px) `DragIndicator` in `text.secondary`, `text.primary` under
//     the pointer, `cursor: grab`, and a 2px primary focus ring offset 2 at
//     radius 4;
//   - **the priority circle**: `min-width` 22 by 22 — so a two-digit index
//     widens it — fully round, primary, white 0.75rem bold, `aria-label`
//     "Priority {n}";
//   - **the label**, body1 at 0.875rem, taking the rest of the row;
//   - **the trailing actions**, the caller's (the sort popover's direction
//     toggle and remove button, #691).
//
// The gesture is rbd's: the lifted row is portalled to `document.body` at full
// opacity and z-index 5000 (so a scrolling, transformed popover cannot clip
// it), the slot it left holds the gap, the others slide over 200ms
// `cubic-bezier(0.2, 0, 0, 1)`, and the drop settles on rbd's distance-scaled
// clock at z-index 4500 — `settleOver`, shared with the strip. While a drag is
// on, the page's cursor is `grabbing` and nothing selects (rbd's body rules).
// A drop on the same index, a drop outside the list, or an Escape writes
// nothing; a real move calls `onReorder(from, to)` once.
//
// **The announcements name the row** — a parity break. The donor keeps rbd's
// defaults (`sort-popover.tsx:235` passes none): "You have lifted an item in
// position 1", "the item" throughout (`rbd:168-236`). The image strip names
// its tile instead (#378, PB-4), and the owner extended that ruling to these
// rows (#698's decision round, D7): "You have lifted Name in position 1".

export interface SortableRowItem {
  id: string;
  /** What the row reads — and what the handle's label names. */
  label: string;
  /** The row's trailing actions. */
  trailing?: React.ReactNode;
}

interface SortableRowListProps {
  items: readonly SortableRowItem[];
  /** Called once per real move, with the lifted row's index and its new one. */
  onReorder: (from: number, to: number) => void;
  /** The list's name — the donor's "Active sort columns". */
  "aria-label": string;
}

type CollisionArgs = Parameters<CollisionDetection>[0];

/** A vertical list walks up and down; rbd ignores ←/→ there. */
const verticalOnlyCoordinates: KeyboardCoordinateGetter = (event, args) =>
  event.code === "ArrowUp" || event.code === "ArrowDown"
    ? sortableKeyboardCoordinates(event, args)
    : undefined;

/** `useSyncExternalStore` wants a subscription; `<body>` never changes. */
const subscribeToNothing = () => () => {};

export function SortableRowList({
  items,
  onReorder,
  "aria-label": ariaLabel,
}: SortableRowListProps) {
  const sensors = useDonorSensors(verticalOnlyCoordinates);
  const [activeId, setActiveId] = React.useState<UniqueIdentifier | null>(null);
  const [landingId, setLandingId] = React.useState<UniqueIdentifier | null>(null);
  const [keyboardDrag, setKeyboardDrag] = React.useState(false);
  const cancelled = React.useRef(false);
  // The gesture, as `settle` reads it: set as the drag starts, so a drop that
  // lands before the render carrying `keyboardDrag` still sees it.
  const byKeyboard = React.useRef(false);
  /** Whether the lifted row has been over anything but its own slot yet. */
  const moved = React.useRef(false);
  const body = React.useSyncExternalStore(
    subscribeToNothing,
    () => document.body,
    () => null,
  );

  useDonorDragKeys(activeId !== null, keyboardDrag);

  useDonorDragBodyRules(activeId !== null);

  const ids = items.map((item) => item.id);
  const indexOf = (id: UniqueIdentifier) => ids.indexOf(String(id));

  const listRef = React.useRef<HTMLDivElement>(null);
  /** The destination the last frame settled on — rbd's displaced set. */
  const settledOver = React.useRef<number | null>(null);

  const donorReorder: CollisionDetection = (args) => {
    const list = listRef.current?.getBoundingClientRect();
    return list ? donorReorderIn(args, ids, list, settledOver) : [];
  };

  // rbd's default sentences (`rbd:168-236`), whitespace collapsed — the live
  // region reads them the same either way — **with the row's label where rbd
  // says "an item"**: the image strip's PB-4 (#378), extended to the sort rows
  // by the owner (#698's decision round, D7). The donor's rows say "an item"
  // because rbd has nothing to call them by; every row here already names its
  // handle ("Drag to reorder Name"). Positions count against the list as it
  // stood at the lift — `items` does not change until the drop commits.
  // A row that has left `items` mid-drag reads as rbd's own words.
  const sentences = donorSentences(
    (id) => items[indexOf(id)]?.label ?? "the item",
    (id) => indexOf(id) + 1,
  );
  const announcements: Announcements = {
    ...sentences,
    onDragOver: (event) => {
      // `@dnd-kit` reports the row as over its own slot the instant it lifts,
      // which would talk over the lift sentence; rbd says nothing until the
      // first move. `undefined` leaves the live region alone.
      if (!moved.current && event.over?.id === event.active.id) return undefined;
      moved.current = true;
      return sentences.onDragOver?.(event);
    },
  };

  const start = ({ active, activatorEvent }: DragStartEvent) => {
    cancelled.current = false;
    moved.current = false;
    settledOver.current = null;
    byKeyboard.current = activatorEvent instanceof KeyboardEvent;
    setKeyboardDrag(byKeyboard.current);
    setLandingId(null);
    setActiveId(active.id);
  };

  const end = ({ active, over }: DragEndEvent) => {
    setLandingId(active.id);
    setActiveId(null);
    const from = indexOf(active.id);
    const to = over ? indexOf(over.id) : -1;
    if (to !== -1 && to !== from) onReorder(from, to);
  };

  const cancel = () => {
    cancelled.current = true;
    setLandingId(activeId);
    setActiveId(null);
  };

  const settle: DropAnimationFunction = (args) =>
    donorSettle(args, { byKeyboard: byKeyboard.current, cancelled: cancelled.current }, () =>
      setLandingId(null),
    );

  const liftedItem = activeId === null ? null : items[indexOf(activeId)];

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={donorReorder}
      // Resting geometry for `donorReorder`: measured at the lift and not again,
      // so no row is read while it carries its own shift. `WhileDragging` at the
      // default `Optimized` frequency measures once as the drag starts and again
      // only when the sortable items change — which these do on the drop, not
      // during the drag (`useDroppableMeasuring`: a re-measure is the drag's
      // start or a `measureDroppableContainers` call, never a pointer move).
      measuring={{ droppable: { strategy: MeasuringStrategy.WhileDragging } }}
      accessibility={{
        announcements,
        screenReaderInstructions: { draggable: DRAG_HANDLE_INSTRUCTIONS },
      }}
      onDragStart={start}
      onDragEnd={end}
      onDragCancel={cancel}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div ref={listRef} role="list" aria-label={ariaLabel}>
          {items.map((item, index) => (
            <SortableRow key={item.id} item={item} index={index} landing={landingId === item.id} />
          ))}
        </div>
      </SortableContext>
      {body
        ? createPortal(
            <DragOverlay zIndex={LIFTED_Z_INDEX} dropAnimation={settle}>
              {liftedItem ? (
                <RowPaper
                  item={liftedItem}
                  index={indexOf(liftedItem.id)}
                  lifted
                  data-slot="sortable-row-lifted"
                  aria-hidden="true"
                />
              ) : null}
            </DragOverlay>,
            body,
          )
        : null}
    </DndContext>
  );
}

/**
 * rbd's default sentences (`rbd:168-236`) for one list, with `nameOf`'s name
 * where rbd says "an item" and `positionOf`'s 1-based position.
 */
function donorSentences(
  nameOf: (id: UniqueIdentifier) => string,
  positionOf: (id: UniqueIdentifier) => number,
): Announcements {
  return {
    onDragStart: ({ active }) =>
      `You have lifted ${nameOf(active.id)} in position ${positionOf(active.id)}`,
    onDragOver: ({ active, over }) =>
      over
        ? `You have moved ${nameOf(active.id)} from position ${positionOf(active.id)} to position ${positionOf(over.id)}`
        : "You are over an area that cannot be dropped on",
    onDragEnd: ({ active, over }) => {
      const name = nameOf(active.id);
      const from = positionOf(active.id);
      return over
        ? `You have dropped ${name}. You have moved ${name} from position ${from} to position ${positionOf(over.id)}`
        : `${name} has been dropped while not over a drop area. ${name} has returned to its starting position of ${from}`;
    },
    onDragCancel: ({ active }) =>
      `Movement cancelled. ${nameOf(active.id)} has returned to its starting position of ${positionOf(active.id)}`,
  };
}

/**
 * Which row of `ids` — one list, drawn at `list` — the lifted one is over: rbd's droppable test, then rbd's reorder
 * rule — the image strip's `donorReorder` (`image-upload-field.tsx`) on the
 * vertical axis, which is the one the donor's sort list runs on.
 *
 * **The droppable test** (`get-droppable-over.js:17-23,95-133`). A release
 * outside the donor's `Droppable` has no destination — "The item has been
 * dropped while not over a drop area" — and writes nothing. `closestCenter`
 * always names a winner however far the pointer has gone, so without this
 * gate a drop well below the list reorders it. rbd's gate is overlap plus a
 * cross-axis test: the row may hang half out of the list sideways and still
 * land.
 *
 * **The reorder rule** (`get-reorder-impact.js:74-116`). rbd moves a row
 * aside when the lifted row's *leading edge* passes its centre, and back when
 * the *trailing edge* does — edge against centre, where `closestCenter` is
 * centre against centre; the `displaced` branches are its hysteresis. The
 * rects are the resting ones (each list's `MeasuringStrategy.WhileDragging`), because the
 * arithmetic is against resting centres.
 */
function donorReorderIn(
  args: CollisionArgs,
  ids: readonly UniqueIdentifier[],
  list: DOMRect,
  settledOver: React.RefObject<number | null>,
): Collision[] {
  const target = args.collisionRect;
  const leftTheList = () => {
    settledOver.current = null;
    return [];
  };

  const overlapsList =
    target.left < list.right &&
    target.right > list.left &&
    target.top < list.bottom &&
    target.bottom > list.top;
  if (!overlapsList) return leftTheList();
  // The cross axis is horizontal on a vertical list.
  const middle = list.left + list.width / 2;
  const centre = { x: target.left + target.width / 2, y: target.top + target.height / 2 };
  const centreInside =
    centre.x >= list.left &&
    centre.x <= list.right &&
    centre.y >= list.top &&
    centre.y <= list.bottom;
  const leftInside = target.left >= list.left && target.left <= list.right;
  const rightInside = target.right >= list.left && target.right <= list.right;
  const overTheList =
    centreInside ||
    // Wider than the list and covering it outright — a row wholly beside
    // the list has already left, at `overlapsList`.
    (!leftInside && !rightInside) ||
    // Otherwise the contained edge has to be past the halfway line.
    (leftInside ? target.left < middle : target.right > middle);
  if (!overTheList) return leftTheList();

  const lifted = ids.indexOf(args.active.id);
  const liftedRect = lifted === -1 ? undefined : args.droppableRects.get(args.active.id);
  if (!liftedRect) return [];
  // rbd displaces by the lifted item's **margin** box (`rbd:7767-7770`), and
  // a row carries its 8px below it — which the strip's gap never had.
  const node = args.droppableContainers.find((container) => container.id === args.active.id)?.node
    .current;
  const margins = node
    ? parseFloat(getComputedStyle(node).marginTop) + parseFloat(getComputedStyle(node).marginBottom)
    : 0;
  const displacement = liftedRect.height + margins;

  const previous = settledOver.current ?? lifted;
  const isDisplaced = (index: number) => (index > lifted ? index > previous : index >= previous);
  let reachedAt: number | null = null;
  for (const [index, id] of ids.entries()) {
    if (index === lifted) continue;
    const rect = args.droppableRects.get(id);
    if (!rect) continue;
    const rowCentre = rect.top + rect.height / 2;
    const displaced = isDisplaced(index);
    const reached =
      index > lifted
        ? displaced
          ? target.bottom <= rowCentre
          : target.top < rowCentre - displacement
        : displaced
          ? target.bottom <= rowCentre + displacement
          : target.top < rowCentre;
    if (reached) {
      reachedAt = index;
      break;
    }
  }
  // An insertion point in the list as it stands; nothing reached is past
  // the end (`get-reorder-impact.js:37-51`).
  const insertAt = reachedAt ?? ids.length;
  const destination = insertAt > lifted ? insertAt - 1 : insertAt;
  settledOver.current = destination;
  const landing = ids[destination];
  return landing === undefined ? [] : [{ id: landing }];
}

/**
 * The settle — rbd's drop, not `@dnd-kit`'s 250ms `ease`: the lifted copy
 * travels from where it was let go to the slot it lands in, over
 * `settleOver` (0.33s → 0.55s with distance, a cancel at 0.6 of that inside
 * the scaled range), on `cubic-bezier(.2,1,.1,1)`, one layer down at 4500.
 * A copy already home does not move at all — rbd skips the animation
 * (`drop-middleware.js:121-131`). The slot stays a gap until it has landed.
 *
 * **A keyboard drop is always home.** rbd's keyboard lift takes the row's
 * own centre as the selection, every arrow recomputes it from the impact,
 * and the drop derives its home offset from that same impact — so Space
 * commits on the keystroke, with no settle (the image strip's
 * `settleTheTile` has the trail). Decided off the gesture rather than off
 * the rects: a Space pressed while the arrow's move is still sliding reads
 * as travel. An Escape is not home — it flies back to the slot it left.
 */
function donorSettle(
  { active, dragOverlay, transform }: Parameters<DropAnimationFunction>[0],
  gesture: { byKeyboard: boolean; cancelled: boolean },
  done: () => void,
): ReturnType<DropAnimationFunction> {
  const { byKeyboard, cancelled } = gesture;
  const delta = {
    x: dragOverlay.rect.left - active.rect.left,
    y: dragOverlay.rect.top - active.rect.top,
  };
  const travel = Math.hypot(delta.x, delta.y);
  if ((byKeyboard && !cancelled) || travel < 0.5) {
    done();
    return;
  }
  dragOverlay.node.style.zIndex = String(SETTLING_Z_INDEX);
  const animation = dragOverlay.node.animate(
    [
      { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` },
      { transform: `translate3d(${transform.x - delta.x}px, ${transform.y - delta.y}px, 0)` },
    ],
    { duration: settleOver(travel, cancelled), easing: DROP_EASING, fill: "forwards" },
  );
  return new Promise<void>((landed) => {
    const arrive = () => {
      done();
      landed();
    };
    animation.onfinish = arrive;
    animation.oncancel = arrive;
  });
}

function SortableRow({
  item,
  index,
  landing,
}: {
  item: SortableRowItem;
  index: number;
  landing: boolean;
}) {
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, transition: SIBLING_SHIFT });

  return (
    <div
      ref={setNodeRef}
      data-sortable-row={item.id}
      className="mb-2"
      style={{
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
        // The gap: the slot keeps its size and paints nothing while its copy
        // is in the air, and until that copy has landed.
        opacity: isDragging || landing ? 0 : undefined,
      }}
    >
      <RowPaper
        item={item}
        index={index}
        handle={{
          ref: setActivatorNodeRef,
          ...attributes,
          ...listeners,
          // `@dnd-kit` calls the handle a "sortable"; rbd's is a plain button,
          // and the image strip takes the same description off (#378).
          "aria-roledescription": undefined,
        }}
      />
    </div>
  );
}

interface RowPaperProps extends React.ComponentProps<"div"> {
  item: SortableRowItem;
  index: number;
  lifted?: boolean;
  /** The drag handle's wiring; absent on the lifted copy, which is inert. */
  handle?: React.ComponentProps<"span"> & { ref?: React.Ref<HTMLSpanElement> };
}

function RowPaper({ item, index, lifted = false, handle, className, ...props }: RowPaperProps) {
  return (
    <div
      role={lifted ? undefined : "listitem"}
      {...props}
      className={cn(
        "border-lennar-divider flex items-center gap-2 rounded-[4px] border px-2 py-1",
        "transition-shadow duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
        lifted ? "bg-lennar-action-hover shadow-mui-elevation-4" : "bg-white shadow-none",
        className,
      )}
    >
      <span
        aria-label={`Drag to reorder ${item.label}`}
        {...handle}
        className={cn(
          "text-lennar-ink-secondary hover:text-lennar-ink inline-flex cursor-grab rounded-[4px] active:cursor-grabbing",
          "focus-visible:outline-lennar-blue-600 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid",
        )}
      >
        <MuiDragIndicatorIcon />
      </span>
      <span
        aria-label={`Priority ${index + 1}`}
        className="bg-lennar-blue-600 inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full text-[0.75rem] font-bold text-white"
      >
        {index + 1}
      </span>
      <p className="m-0 flex-1 text-[0.875rem] leading-normal">{item.label}</p>
      {item.trailing}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The whole-row mode: two levels, the whole card or row the handle (#805)
// ---------------------------------------------------------------------------
//
// The Editor's drag — the donor's one `DragDropContext` around the version's
// groups (`SpecEditIsland.tsx:200-237`): a `groups-grid` droppable of type
// `droppable-group`, each group a `DraggableRow` whose whole box is the handle
// (`draggable-row.tsx:16-19` spreads `draggableProps` and `dragHandleProps` on
// one `Box`), holding its own `droppable-lines-{groupId}` droppable of line
// rows that are `DraggableRow`s too (`EditableGroup.tsx:91`). The gesture,
// keys, motion and settle are the sort rows' above, unchanged; what this mode
// adds:
//
//   - **the whole card is the handle**, its content the caller's. It is
//     focusable and described by rbd's instructions, but carries no `role`:
//     rbd's `role="button"` would make every control inside it presentational
//     to a screen reader (#805 Assumption 5, a recorded a11y-tree-only break).
//     A press or a Space that starts on one of rbd's controls inside never
//     lifts it (`isPressOnInteractiveElement`, in the shared sensors); from
//     anything else focused inside — a note's link, a `role="button"` span —
//     the Space lifts the card, as rbd's window-level keyboard sensor does;
//   - **two levels.** A dragged card's targets are the rendered cards; a
//     dragged line's are its own group's lines only — rbd's per-group `type` —
//     so over another group it has no destination and its drop writes nothing.
//     Without `onMoveLine` the lines are not draggable at all, and a press on
//     one lifts its card;
//   - **the drop reports the move**: the moved id and its new predecessor among
//     the rendered siblings, `null` when it lands first (structure rulings R2)
//     — never an index;
//   - **the lifted copy** carries the donor's dragging dress: a 1px `#F5F5F5`
//     border on the dragged box and a 16px `#D0CFCF` `Dehaze` absolute at
//     `top: 30px` for a card, `48px` for a line (`draggable-row.tsx:21-31`). In
//     the donor that icon sits *before* the card in the box, so a card whose
//     header paints over it hides it — measured on the running donor, the
//     lifted card shows only the border. It is drawn the same way here, and
//     the same paint hides it;
//   - **the announcements name the card or line**, as the sort rows' do (#378
//     PB-4, #698 D7), with positions counted within its own level.

export interface SortableEntry {
  id: string;
  /** What the card or row reads — the name the announcements use. */
  label: string;
}

export interface SortableGroup<L extends SortableEntry = SortableEntry> extends SortableEntry {
  /** The group's rendered lines, in order. */
  lines: readonly L[];
}

/** A drop: the moved row and the sibling it now follows, `null` for first. */
export interface SortableMove {
  id: string;
  afterId: string | null;
}

export interface SortableLineMove extends SortableMove {
  groupId: string;
}

export interface SortableGroupParts {
  /** The group's lines in their own drop list — where the card puts its body. */
  lines: React.ReactNode;
  /** The copy in the air rather than the card in its slot. */
  lifted: boolean;
}

interface NestedSortableListProps<L extends SortableEntry, G extends SortableGroup<L>> {
  groups: readonly G[];
  /** The groups list's name. */
  "aria-label": string;
  renderGroup: (group: G, parts: SortableGroupParts) => React.ReactNode;
  renderLine?: (line: L, group: G) => React.ReactNode;
  onMoveGroup: (move: SortableMove) => void;
  /** Without it the lines stay where they are, and a press on one is its card's. */
  onMoveLine?: (move: SortableLineMove) => void;
  /**
   * rbd's fluid edge scroll in place of `@dnd-kit`'s (`useDonorFluidAutoScroll`,
   * #869 D4 a): the window, then `region` — the caller's scroll container, the
   * Editor's pane. Absent, the drag keeps `@dnd-kit`'s.
   */
  donorAutoScroll?: { region: HTMLElement | null };
}

/** What each sortable carries, so the collision and the drop know its level. */
type EntryData = { kind: "group"; id: string } | { kind: "line"; id: string; groupId: string };

const GROUPS_CONTAINER = "sortable-groups";
const groupKey = (id: string) => `group:${id}`;
const lineKey = (id: string) => `line:${id}`;
const linesContainer = (groupId: string) => `sortable-lines:${groupId}`;

/**
 * The arrows walk the lifted row's own level, up and down only: `@dnd-kit`'s
 * sortable walk over a view of the droppables that holds its siblings alone,
 * so a card never steps onto a line, nor a line into another group.
 */
const siblingsOnlyCoordinates: KeyboardCoordinateGetter = (event, args) => {
  if (event.code !== "ArrowUp" && event.code !== "ArrowDown") return undefined;
  const { droppableContainers } = args.context;
  const containerOf = (id: UniqueIdentifier) =>
    (
      droppableContainers.get(id)?.data.current as
        { sortable?: { containerId: string } } | undefined
    )?.sortable?.containerId;
  const level = containerOf(args.active);
  const siblings = droppableContainers
    .getEnabled()
    .filter((container) => containerOf(container.id) === level);
  const view = {
    getEnabled: () => siblings,
    get: (id: UniqueIdentifier) => droppableContainers.get(id),
  } as unknown as typeof droppableContainers;
  return sortableKeyboardCoordinates(event, {
    ...args,
    context: { ...args.context, droppableContainers: view },
  });
};

/** `@dnd-kit`'s handle attributes, less what rbd's handle does not say. */
function handleAttributes(attributes: ReturnType<typeof useSortable>["attributes"]) {
  const {
    role: _role,
    "aria-roledescription": _roledescription,
    "aria-pressed": _pressed,
    "aria-disabled": _disabled,
    ...rest
  } = attributes;
  return rest;
}

export function NestedSortableList<L extends SortableEntry, G extends SortableGroup<L>>({
  groups,
  "aria-label": ariaLabel,
  renderGroup,
  renderLine,
  onMoveGroup,
  onMoveLine,
  donorAutoScroll,
}: NestedSortableListProps<L, G>) {
  const sensors = useDonorSensors(siblingsOnlyCoordinates);
  const [active, setActive] = React.useState<EntryData | null>(null);
  // The copy settling into its slot, and whether a cancel sent it there.
  const [landing, setLanding] = React.useState<{
    key: UniqueIdentifier;
    cancelled: boolean;
  } | null>(null);
  const landingKey = landing?.key ?? null;
  const [keyboardDrag, setKeyboardDrag] = React.useState(false);
  const cancelled = React.useRef(false);
  const byKeyboard = React.useRef(false);
  const moved = React.useRef(false);
  const settledOver = React.useRef<number | null>(null);
  const groupsRef = React.useRef<HTMLDivElement>(null);
  const lineLists = React.useRef(new Map<string, HTMLElement>());
  const body = React.useSyncExternalStore(
    subscribeToNothing,
    () => document.body,
    () => null,
  );

  useDonorDragKeys(active !== null, keyboardDrag);
  useDonorDragBodyRules(active !== null);
  // rbd's keyboard drag scrolls by its jump scroller, not this curve; the
  // keyboard sensor's own scrolling stays.
  const liftedBox = React.useRef<HTMLDivElement>(null);
  useDonorFluidAutoScroll(
    donorAutoScroll !== undefined && active !== null && !keyboardDrag,
    liftedBox,
    donorAutoScroll?.region ?? null,
    // rbd measures only the lists of the dragged row's type, and each group's
    // lines are a type of their own (`droppable-lines-${groupId}`).
    () => {
      const list =
        active?.kind === "line" ? lineLists.current.get(active.groupId) : groupsRef.current;
      return list ? [list] : [];
    },
  );

  const groupKeys = groups.map((group) => groupKey(group.id));
  const linesOf = (groupId: string) => groups.find((group) => group.id === groupId)?.lines ?? [];
  const lineKeysOf = (groupId: string) => linesOf(groupId).map((line) => lineKey(line.id));
  const dataOf = (data: unknown) => data as EntryData | undefined;

  /** The lifted row's own level: its keys, and the list element they sit in. */
  const levelOf = (data: EntryData | undefined) =>
    data?.kind === "line"
      ? { keys: lineKeysOf(data.groupId), list: lineLists.current.get(data.groupId) ?? null }
      : { keys: groupKeys, list: groupsRef.current };

  const donorReorder: CollisionDetection = (args) => {
    const { keys, list } = levelOf(dataOf(args.active.data.current));
    const rect = list?.getBoundingClientRect();
    return rect ? donorReorderIn(args, keys, rect, settledOver) : [];
  };

  const entryOf = (id: UniqueIdentifier): { label: string; position: number } | undefined => {
    const key = String(id);
    const groupIndex = groupKeys.indexOf(key);
    if (groupIndex !== -1) return { label: groups[groupIndex]!.label, position: groupIndex + 1 };
    for (const group of groups) {
      const index = group.lines.findIndex((line) => lineKey(line.id) === key);
      if (index !== -1) return { label: group.lines[index]!.label, position: index + 1 };
    }
    return undefined;
  };
  const sentences = donorSentences(
    (id) => entryOf(id)?.label ?? "the item",
    (id) => entryOf(id)?.position ?? 0,
  );
  const announcements: Announcements = {
    ...sentences,
    onDragOver: (event) => {
      // `@dnd-kit` reports the row as over its own slot the instant it lifts,
      // which would talk over the lift sentence; rbd says nothing until the
      // first move. `undefined` leaves the live region alone.
      if (!moved.current && event.over?.id === event.active.id) return undefined;
      moved.current = true;
      return sentences.onDragOver?.(event);
    },
  };

  const start = ({ active: lifted, activatorEvent }: DragStartEvent) => {
    cancelled.current = false;
    moved.current = false;
    settledOver.current = null;
    byKeyboard.current = activatorEvent instanceof KeyboardEvent;
    setKeyboardDrag(byKeyboard.current);
    setLanding(null);
    setActive(dataOf(lifted.data.current) ?? null);
  };

  const end = ({ active: dropped, over }: DragEndEvent) => {
    setLanding({ key: dropped.id, cancelled: false });
    setActive(null);
    const data = dataOf(dropped.data.current);
    if (!data || !over) return;
    const { keys } = levelOf(data);
    const droppedKey = String(dropped.id);
    const from = keys.indexOf(droppedKey);
    const to = keys.indexOf(String(over.id));
    if (from === -1 || to === -1 || from === to) return;
    const order = keys.filter((key) => key !== droppedKey);
    order.splice(to, 0, droppedKey);
    const after = to === 0 ? null : order[to - 1]!;
    const afterId = after === null ? null : after.slice(after.indexOf(":") + 1);
    if (data.kind === "group") onMoveGroup({ id: data.id, afterId });
    else onMoveLine?.({ id: data.id, groupId: data.groupId, afterId });
  };

  const cancel = () => {
    cancelled.current = true;
    setLanding(
      active
        ? {
            key: active.kind === "group" ? groupKey(active.id) : lineKey(active.id),
            cancelled: true,
          }
        : null,
    );
    setActive(null);
  };

  const settle: DropAnimationFunction = (args) =>
    donorSettle(args, { byKeyboard: byKeyboard.current, cancelled: cancelled.current }, () =>
      setLanding(null),
    );

  const phase: DonorDragPhase =
    active !== null
      ? "dragging"
      : landing === null
        ? "resting"
        : landing.cancelled
          ? "userCancel"
          : "dropAnimating";

  /** A group's lines as they rest in the card: sortable, or just drawn. */
  const restingLines = (group: G) => (
    <div
      ref={(node) => {
        if (node) lineLists.current.set(group.id, node);
        else lineLists.current.delete(group.id);
      }}
      role="list"
      aria-label={`${group.label} lines`}
    >
      {onMoveLine ? (
        <SortableContext
          id={linesContainer(group.id)}
          items={lineKeysOf(group.id)}
          strategy={verticalListSortingStrategy}
        >
          {group.lines.map((line) => (
            <SortableWholeRow
              key={line.id}
              entry={{ kind: "line", id: line.id, groupId: group.id }}
              marker={{ "data-sortable-line": line.id }}
              landing={landingKey === lineKey(line.id)}
              phase={phase}
            >
              {renderLine?.(line, group)}
            </SortableWholeRow>
          ))}
        </SortableContext>
      ) : (
        group.lines.map((line) => (
          <div key={line.id} role="listitem" data-sortable-line={line.id}>
            {renderLine?.(line, group)}
          </div>
        ))
      )}
    </div>
  );

  /** The same lines in the air: drawn, nothing sortable, nothing named. */
  const liftedLines = (group: G) => (
    <div>
      {group.lines.map((line) => (
        <div key={line.id}>{renderLine?.(line, group)}</div>
      ))}
    </div>
  );

  const liftedGroup =
    active?.kind === "group" ? groups.find((group) => group.id === active.id) : undefined;
  const liftedLineGroup =
    active?.kind === "line" ? groups.find((group) => group.id === active.groupId) : undefined;
  const liftedLine =
    active?.kind === "line"
      ? liftedLineGroup?.lines.find((line) => line.id === active.id)
      : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={donorReorder}
      autoScroll={donorAutoScroll === undefined}
      // Resting geometry, as the sort rows measure it — see `SortableRowList`.
      measuring={{ droppable: { strategy: MeasuringStrategy.WhileDragging } }}
      accessibility={{
        announcements,
        screenReaderInstructions: { draggable: DRAG_HANDLE_INSTRUCTIONS },
      }}
      onDragStart={start}
      onDragEnd={end}
      onDragCancel={cancel}
    >
      <SortableContext
        id={GROUPS_CONTAINER}
        items={groupKeys}
        strategy={verticalListSortingStrategy}
      >
        <div ref={groupsRef} role="list" aria-label={ariaLabel}>
          {groups.map((group) => (
            <SortableWholeRow
              key={group.id}
              entry={{ kind: "group", id: group.id }}
              marker={{ "data-sortable-card": group.id }}
              landing={landingKey === groupKey(group.id)}
              phase={phase}
            >
              {renderGroup(group, { lines: restingLines(group), lifted: false })}
            </SortableWholeRow>
          ))}
        </div>
      </SortableContext>
      {body
        ? createPortal(
            <DragOverlay
              zIndex={LIFTED_Z_INDEX}
              dropAnimation={settle}
              // rbd's dragged box is `pointer-events: none` (`esm:7726-7737`):
              // nothing on the copy hovers or answers while it is in the air.
              className="pointer-events-none"
            >
              {liftedGroup ? (
                <LiftedBox kind="card" ref={liftedBox}>
                  {renderGroup(liftedGroup, { lines: liftedLines(liftedGroup), lifted: true })}
                </LiftedBox>
              ) : liftedLine && liftedLineGroup ? (
                <LiftedBox kind="line" ref={liftedBox}>
                  {renderLine?.(liftedLine, liftedLineGroup)}
                </LiftedBox>
              ) : null}
            </DragOverlay>,
            body,
          )
        : null}
    </DndContext>
  );
}

/**
 * The browser's own focus ring on a card or line, as rbd's handle shows it.
 * The app-wide base rule (`index.css`, `* { outline-ring/50 }`) recolours the
 * ring every focusable element would otherwise get; `revert` hands the colour
 * back to the browser's `:focus-visible` rule.
 */
const HANDLE_FOCUS_RING = "[outline-color:revert]";

/**
 * One card in its slot, or one line in its group's list: the whole of it the
 * handle, and its own keyboard activator — the key that lifts it may land on
 * anything inside it the donor's sensor would not refuse (`DonorKeyboardSensor`).
 */
function SortableWholeRow({
  entry,
  landing,
  phase,
  marker,
  children,
}: {
  entry: EntryData;
  landing: boolean;
  phase: DonorDragPhase;
  /** The row's `data-sortable-card` or `data-sortable-line`. */
  marker: { "data-sortable-card": string } | { "data-sortable-line": string };
  children: React.ReactNode;
}) {
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: entry.kind === "group" ? groupKey(entry.id) : lineKey(entry.id),
    data: entry,
    transition: SIBLING_SHIFT,
  });
  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        setActivatorNodeRef(node);
      }}
      role="listitem"
      {...marker}
      className={cn(
        donorHandleClasses(phase),
        HANDLE_FOCUS_RING,
        // The slot a copy is landing in is not there yet: in the donor the
        // landing item is the one in the air, under nothing and no handle.
        landing && "pointer-events-none",
      )}
      {...handleAttributes(attributes)}
      {...listeners}
      style={{
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
        // The gap: the slot keeps its size and paints nothing while its copy
        // is in the air, and until that copy has landed.
        opacity: isDragging || landing ? 0 : undefined,
      }}
    >
      {children}
    </div>
  );
}

/**
 * The dragged box's dress (`draggable-row.tsx:21-31`): the 1px `#F5F5F5`
 * border, and the `Dehaze` before the content — absolute, no `left`, so it
 * stands at the box's left edge, `top: 30px` for a card and `48px` for a line.
 * Hidden from assistive tech: the slot it left is still the real row.
 */
function LiftedBox({
  kind,
  ref,
  children,
}: {
  kind: "card" | "line";
  /** The dragged box the edge scroll keys on. */
  ref?: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
}) {
  return (
    <div
      ref={ref}
      data-slot={kind === "card" ? "sortable-card-lifted" : "sortable-line-lifted"}
      aria-hidden="true"
      // `relative` stands in for the donor box's `position: fixed` while it
      // drags: the icon's `top` is measured inside its border, 31px down.
      // `h-full`: rbd fixes the box at the border-box height it measured at
      // rest (`esm:7726-7732`), so its border hugs the card and the card's
      // own bottom margin hangs outside it.
      className="border-mui-grey-100 relative h-full border border-solid"
    >
      <MuiDehazeIcon
        className={cn(
          "text-spec-drag-mark absolute",
          kind === "card" ? "top-[30px]" : "top-[48px]",
        )}
      />
      {children}
    </div>
  );
}
